import { useEffect, useId, useRef, useState } from "react";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";

import { ULThemeFloatingLabelField } from "@/components/form/ULThemeFloatingLabelField";
import {
  type AddressSuggestion,
  fetchAddressSuggestions,
  GEOAPIFY_API_KEY,
  MIN_QUERY_LENGTH,
} from "@/utils/geoapify";

// Wait for a pause in typing before calling Geoapify. Every request is a
// credit, and per-keystroke lookups would spend ~3x more for the same result.
const DEBOUNCE_MS = 300;

interface AddressAutocompleteFieldProps<T extends FieldValues> {
  field: ControllerRenderProps<T, Path<T>>;
  label: string;
  error?: boolean;
  autoComplete?: string;
  // Overridable for tests; defaults to the build-time key. No key = a plain
  // text field with no lookups at all.
  apiKey?: string;
}

// A free-text address field with Geoapify suggestions underneath.
//
// Suggestions are an aid, never a gate: the member can ignore them and type
// any address, and a failed or rate-limited lookup just shows nothing. The
// field value is always the plain string in the input, so validation and
// submission are identical to the plain field this replaces.
function AddressAutocompleteField<T extends FieldValues>({
  field,
  label,
  error,
  autoComplete,
  apiKey = GEOAPIFY_API_KEY,
}: AddressAutocompleteFieldProps<T>) {
  const listboxId = useId();
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  // The text the member last typed. Picking a suggestion changes the value
  // without changing this, so it doesn't trigger a fresh lookup for the
  // address they just chose.
  const [query, setQuery] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!apiKey || query.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      fetchAddressSuggestions(query.trim(), apiKey, controller.signal)
        .then((results) => {
          setSuggestions(results);
          setHighlighted(-1);
          setOpen(results.length > 0);
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name === "AbortError") return;
          // Out of credits, offline, origin not allowed on the key — all
          // degrade to "no suggestions". The member keeps typing.
          setSuggestions([]);
          setOpen(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, apiKey]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const select = (suggestion: AddressSuggestion) => {
    field.onChange(suggestion.formatted);
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && highlighted >= 0) {
      // Only swallow Enter when it is picking a suggestion; otherwise leave
      // the wizard's own Enter handling alone.
      e.preventDefault();
      select(suggestions[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showList = open && suggestions.length > 0;

  return (
    <div className="relative">
      <ULThemeFloatingLabelField
        {...field}
        value={field.value ?? ""}
        onChange={(e) => {
          field.onChange(e);
          setQuery(e.target.value);
        }}
        onBlur={() => {
          setOpen(false);
          field.onBlur();
        }}
        onFocus={() => setOpen(suggestions.length > 0)}
        onKeyDown={handleKeyDown}
        label={label}
        type="text"
        autoComplete={apiKey ? "off" : autoComplete}
        role={apiKey ? "combobox" : undefined}
        aria-autocomplete={apiKey ? "list" : undefined}
        aria-expanded={apiKey ? showList : undefined}
        aria-controls={apiKey ? listboxId : undefined}
        aria-activedescendant={
          showList && highlighted >= 0
            ? `${listboxId}-${highlighted}`
            : undefined
        }
        error={error}
      />

      {showList && (
        <div
          className="absolute left-0 right-0 z-50 -mt-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
          // Keep focus in the input so blur doesn't close the list before the
          // click on an option registers.
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul id={listboxId} role="listbox" aria-label={label}>
            {suggestions.map((s, i) => (
              <li
                key={s.id}
                id={`${listboxId}-${i}`}
                role="option"
                aria-selected={i === highlighted}
                className={`cursor-pointer px-4 py-2 text-left text-sm ${
                  i === highlighted ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
                onMouseEnter={() => setHighlighted(i)}
                onClick={() => select(s)}
              >
                <div className="text-gray-900">{s.line1}</div>
                {s.line2 && (
                  <div className="text-xs text-gray-500">{s.line2}</div>
                )}
              </li>
            ))}
          </ul>
          {/* Attribution is a condition of Geoapify's free plan. */}
          <div className="border-t border-gray-100 px-4 py-1 text-right text-[11px] text-gray-400">
            Powered by{" "}
            <a
              href="https://www.geoapify.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Geoapify
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddressAutocompleteField;
